// src/FarmerProfile.js
import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

export default function FarmerProfile() {
  const { user } = useAuth();
  const [farmer, setFarmer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    country: '',
    region: '',
    district: '',
    village: '',
    gps_lat: '',
    gps_lng: '',
    plot_size_ha: '',
    land_type: '',
    current_crops: '',
    past_crops: '',
    future_crops: '',
    diversification_plans: '',
    people_living: '',
    people_working: '',
    work_type: '',
    land_worked_duration: '',
    seed_source: '',
    seed_type: '',
    fertilizers_used: '',
    pesticide_history: '',
    past_risks: '',
    major_challenges: '',
    farmer_notes: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/farmers/profile', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.ok && data.farmer) {
        setFarmer(data.farmer);
        setFormData({
          full_name: data.farmer.full_name || '',
          country: data.farmer.country || '',
          region: data.farmer.region || '',
          district: data.farmer.district || '',
          village: data.farmer.village || '',
          gps_lat: data.farmer.gps_lat || '',
          gps_lng: data.farmer.gps_lng || '',
          plot_size_ha: data.farmer.plot_size_ha || '',
          land_type: data.farmer.land_type || '',
          current_crops: data.farmer.current_crops || '',
          past_crops: data.farmer.past_crops || '',
          future_crops: data.farmer.future_crops || '',
          diversification_plans: data.farmer.diversification_plans || '',
          people_living: data.farmer.people_living || '',
          people_working: data.farmer.people_working || '',
          work_type: data.farmer.work_type || '',
          land_worked_duration: data.farmer.land_worked_duration || '',
          seed_source: data.farmer.seed_source || '',
          seed_type: data.farmer.seed_type || '',
          fertilizers_used: data.farmer.fertilizers_used || '',
          pesticide_history: data.farmer.pesticide_history || '',
          past_risks: data.farmer.past_risks || '',
          major_challenges: data.farmer.major_challenges || '',
          farmer_notes: data.farmer.farmer_notes || ''
        });
        setEditing(false);
      } else {
        // No profile yet, enable editing mode
        setEditing(true);
      }
    } catch (err) {
      setError('Failed to load profile: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/farmers/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.ok) {
        setFarmer(data.farmer);
        setSuccess('Profile updated successfully!');
        setEditing(false);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to update profile');
      }
    } catch (err) {
      setError('Failed to save profile: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'white' }}>
        Loading profile...
      </div>
    );
  }

  return (
    <div style={{
      background: '#0f2419',
      minHeight: '100vh',
      padding: '20px',
      color: 'white'
    }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '1px solid #2d5a45'
        }}>
          <div>
            <h1 style={{ margin: 0 }}>My Farm Profile</h1>
            <p style={{ margin: '8px 0 0 0', color: '#888' }}>
              {user?.email}
            </p>
          </div>
          {!editing && farmer && (
            <button
              onClick={() => setEditing(true)}
              style={{
                padding: '10px 20px',
                background: '#4ade80',
                color: '#0f2419',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Edit Profile
            </button>
          )}
        </div>

        {/* Messages */}
        {error && (
          <div style={{
            background: '#fee2e2',
            color: '#991b1b',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            background: '#d1fae5',
            color: '#065f46',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            {success}
          </div>
        )}

        {/* Profile Form */}
        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Section title="Basic Information">
            <InputField
              label="Full Name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              disabled={!editing}
              required
            />
            <InputField
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              disabled={!editing}
            />
            <InputField
              label="Region"
              name="region"
              value={formData.region}
              onChange={handleChange}
              disabled={!editing}
            />
            <InputField
              label="District"
              name="district"
              value={formData.district}
              onChange={handleChange}
              disabled={!editing}
            />
            <InputField
              label="Village"
              name="village"
              value={formData.village}
              onChange={handleChange}
              disabled={!editing}
            />
          </Section>

          {/* Land & Location */}
          <Section title="Land & Location">
            <InputField
              label="Plot Size (hectares)"
              name="plot_size_ha"
              type="number"
              step="0.01"
              value={formData.plot_size_ha}
              onChange={handleChange}
              disabled={!editing}
            />
            <SelectField
              label="Land Type"
              name="land_type"
              value={formData.land_type}
              onChange={handleChange}
              disabled={!editing}
              options={[
                { value: '', label: 'Select land type' },
                { value: 'Grassland', label: 'Grassland' },
                { value: 'Tropical', label: 'Tropical' },
                { value: 'Coastal', label: 'Coastal' },
                { value: 'Mountainous', label: 'Mountainous' },
                { value: 'Desert', label: 'Desert' },
                { value: 'Other', label: 'Other' }
              ]}
            />
            <InputField
              label="GPS Latitude"
              name="gps_lat"
              type="number"
              step="0.000001"
              value={formData.gps_lat}
              onChange={handleChange}
              disabled={!editing}
            />
            <InputField
              label="GPS Longitude"
              name="gps_lng"
              type="number"
              step="0.000001"
              value={formData.gps_lng}
              onChange={handleChange}
              disabled={!editing}
            />
          </Section>

          {/* Crops & Livestock */}
          <Section title="Crops & Livestock">
            <TextAreaField
              label="Current Crops/Livestock"
              name="current_crops"
              value={formData.current_crops}
              onChange={handleChange}
              disabled={!editing}
              placeholder="List your current crops or livestock..."
            />
            <TextAreaField
              label="Past Crops (Previous Season)"
              name="past_crops"
              value={formData.past_crops}
              onChange={handleChange}
              disabled={!editing}
              placeholder="What did you grow in the previous season?"
            />
            <TextAreaField
              label="Planned Future Crops/Rotation"
              name="future_crops"
              value={formData.future_crops}
              onChange={handleChange}
              disabled={!editing}
              placeholder="What do you plan to grow next?"
            />
            <TextAreaField
              label="Diversification Plans"
              name="diversification_plans"
              value={formData.diversification_plans}
              onChange={handleChange}
              disabled={!editing}
              placeholder="How are you diversifying your farm?"
            />
          </Section>

          {/* People & Labor */}
          <Section title="People & Labor">
            <InputField
              label="Number of People Living on Land"
              name="people_living"
              type="number"
              value={formData.people_living}
              onChange={handleChange}
              disabled={!editing}
            />
            <InputField
              label="Number of People Working on Land"
              name="people_working"
              type="number"
              value={formData.people_working}
              onChange={handleChange}
              disabled={!editing}
            />
            <SelectField
              label="Work Type"
              name="work_type"
              value={formData.work_type}
              onChange={handleChange}
              disabled={!editing}
              options={[
                { value: '', label: 'Select work type' },
                { value: 'Family-run', label: 'Family-run' },
                { value: 'Cooperative', label: 'Cooperative' },
                { value: 'Full-time', label: 'Full-time' },
                { value: 'Part-time', label: 'Part-time' },
                { value: 'Seasonal', label: 'Seasonal' }
              ]}
            />
            <InputField
              label="How Long Has Land Been Worked?"
              name="land_worked_duration"
              value={formData.land_worked_duration}
              onChange={handleChange}
              disabled={!editing}
              placeholder="e.g., '5 years' or '3 generations'"
            />
          </Section>

          {/* Agricultural Practices */}
          <Section title="Agricultural Practices">
            <TextAreaField
              label="Seed Source"
              name="seed_source"
              value={formData.seed_source}
              onChange={handleChange}
              disabled={!editing}
              placeholder="Where do you get your seeds?"
            />
            <SelectField
              label="Seed Type"
              name="seed_type"
              value={formData.seed_type}
              onChange={handleChange}
              disabled={!editing}
              options={[
                { value: '', label: 'Select seed type' },
                { value: 'Hybrid', label: 'Hybrid' },
                { value: 'Local', label: 'Local' },
                { value: 'Heirloom', label: 'Heirloom' },
                { value: 'Mixed', label: 'Mixed' }
              ]}
            />
            <TextAreaField
              label="Fertilizers Used"
              name="fertilizers_used"
              value={formData.fertilizers_used}
              onChange={handleChange}
              disabled={!editing}
              placeholder="List the fertilizers you use..."
            />
            <TextAreaField
              label="Pesticide History"
              name="pesticide_history"
              value={formData.pesticide_history}
              onChange={handleChange}
              disabled={!editing}
              placeholder="What pesticides have you used, if any?"
            />
          </Section>

          {/* Challenges & Risks */}
          <Section title="Challenges & Risks">
            <TextAreaField
              label="Past Risks or Challenges"
              name="past_risks"
              value={formData.past_risks}
              onChange={handleChange}
              disabled={!editing}
              placeholder="e.g., Drought, flooding, pests, etc."
            />
            <TextAreaField
              label="Major Challenges"
              name="major_challenges"
              value={formData.major_challenges}
              onChange={handleChange}
              disabled={!editing}
              placeholder="What are your biggest farming challenges?"
            />
          </Section>

          {/* Additional Notes */}
          <Section title="Additional Notes">
            <TextAreaField
              label="Notes & Comments"
              name="farmer_notes"
              value={formData.farmer_notes}
              onChange={handleChange}
              disabled={!editing}
              placeholder="Any additional information you'd like to share..."
              rows={6}
            />
          </Section>

          {/* Action Buttons */}
          {editing && (
            <div style={{
              display: 'flex',
              gap: '10px',
              marginTop: '30px',
              paddingTop: '20px',
              borderTop: '1px solid #2d5a45'
            }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '12px 24px',
                  background: saving ? '#888' : '#4ade80',
                  color: '#0f2419',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
              {farmer && (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    fetchProfile();
                  }}
                  style={{
                    padding: '12px 24px',
                    background: 'transparent',
                    color: '#888',
                    border: '1px solid #2d5a45',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '16px'
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

// Helper Components
function Section({ title, children }) {
  return (
    <div style={{
      background: '#1a3327',
      padding: '24px',
      borderRadius: '8px',
      border: '1px solid #2d5a45',
      marginBottom: '24px'
    }}>
      <h2 style={{
        margin: '0 0 20px 0',
        color: '#4ade80',
        fontSize: '20px'
      }}>
        {title}
      </h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '16px'
      }}>
        {children}
      </div>
    </div>
  );
}

function InputField({ label, name, value, onChange, disabled, type = 'text', required, placeholder, step }) {
  return (
    <div>
      <label style={{
        display: 'block',
        color: '#a0a0a0',
        marginBottom: '6px',
        fontSize: '14px'
      }}>
        {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        step={step}
        style={{
          width: '100%',
          padding: '10px',
          background: disabled ? '#0f2419' : '#0f2419',
          border: `1px solid ${disabled ? '#1a3327' : '#2d5a45'}`,
          borderRadius: '4px',
          color: disabled ? '#666' : 'white',
          fontSize: '14px',
          cursor: disabled ? 'not-allowed' : 'text'
        }}
      />
    </div>
  );
}

function SelectField({ label, name, value, onChange, disabled, options, required }) {
  return (
    <div>
      <label style={{
        display: 'block',
        color: '#a0a0a0',
        marginBottom: '6px',
        fontSize: '14px'
      }}>
        {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        required={required}
        style={{
          width: '100%',
          padding: '10px',
          background: disabled ? '#0f2419' : '#0f2419',
          border: `1px solid ${disabled ? '#1a3327' : '#2d5a45'}`,
          borderRadius: '4px',
          color: disabled ? '#666' : 'white',
          fontSize: '14px',
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
      >
        {options.map((opt, idx) => (
          <option key={idx} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextAreaField({ label, name, value, onChange, disabled, required, placeholder, rows = 3 }) {
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <label style={{
        display: 'block',
        color: '#a0a0a0',
        marginBottom: '6px',
        fontSize: '14px'
      }}>
        {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
      </label>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        rows={rows}
        style={{
          width: '100%',
          padding: '10px',
          background: disabled ? '#0f2419' : '#0f2419',
          border: `1px solid ${disabled ? '#1a3327' : '#2d5a45'}`,
          borderRadius: '4px',
          color: disabled ? '#666' : 'white',
          fontSize: '14px',
          fontFamily: 'inherit',
          resize: 'vertical',
          cursor: disabled ? 'not-allowed' : 'text'
        }}
      />
    </div>
  );
}